import { ref, set, onValue, push, update, get, remove } from 'firebase/database';
import { realtimeDb } from './config';
import { Donation } from './donationService';

// Realtime Database paths
const DONATIONS_PATH = 'donations';
const DONATION_STATS_PATH = 'stats';

/**
 * Initialize the database structure if it doesn't exist
 */
const initializeDatabase = async (): Promise<void> => {
  try {
    console.log('Checking if database needs initialization...');

    // Check if stats node exists
    const statsRef = ref(realtimeDb, DONATION_STATS_PATH);
    const statsSnapshot = await get(statsRef);

    // If stats don't exist, create initial structure
    if (!statsSnapshot.exists()) {
      console.log('Initializing database structure...');
      await set(statsRef, {
        totalCollected: 0,
        targetAmount: 100000,
        donationCount: 0,
        pendingDonations: 0
      });
      console.log('Database initialized successfully');
    } else {
      console.log('Database already initialized');
    }
  } catch (error) {
    console.error('Error initializing database:', error);
  }
};

// Initialize the database when this module is loaded
initializeDatabase();

/**
 * Save a new donation to Realtime Database
 */
export const saveRealtimeDonation = async (donationData: Omit<Donation, 'id'>, firestoreId: string): Promise<string> => {
  try {
    // Create a new donation reference with an auto-generated key
    const newDonationRef = push(ref(realtimeDb, DONATIONS_PATH));

    // Save the donation data with the Firestore ID for reference
    await set(newDonationRef, {
      ...donationData,
      firestoreId,
      createdAt: new Date().toISOString()
    });

    // Update the donation stats in realtime
    await updateDonationStats(donationData.amount, donationData.status === 'completed');

    return newDonationRef.key || '';
  } catch (error) {
    console.error('Error saving donation to Realtime Database:', error);
    throw error;
  }
};

/**
 * Update donation stats in Realtime Database
 */
export const updateDonationStats = async (
  amount: number,
  isCompleted: boolean
): Promise<void> => {
  try {
    console.log(`Updating donation stats with amount: ₹${amount}, status: ${isCompleted ? 'completed' : 'pending'}`);

    // Get the current stats
    const statsRef = ref(realtimeDb, DONATION_STATS_PATH);
    const statsSnapshot = await get(statsRef);

    const currentStats = statsSnapshot.exists()
      ? statsSnapshot.val()
      : { totalCollected: 0, targetAmount: 100000, donationCount: 0, pendingDonations: 0 };

    console.log('Current stats before update:', currentStats);

    // Update the stats
    const newStats = {
      ...currentStats,
      donationCount: currentStats.donationCount + 1,
      // Always add the amount to total collected regardless of status
      totalCollected: currentStats.totalCollected + amount,
      pendingDonations: isCompleted
        ? currentStats.pendingDonations
        : currentStats.pendingDonations + 1
    };

    console.log('New stats after update:', newStats);

    // Use transaction to ensure atomic updates
    await set(statsRef, newStats);
    console.log('Stats updated successfully in Realtime Database');

    return newStats;
  } catch (error) {
    console.error('Error updating donation stats:', error);
    throw error;
  }
};

/**
 * Update a donation's status in Realtime Database
 */
export const updateRealtimeDonationStatus = async (
  donationId: string,
  status: 'pending' | 'completed',
  amount: number
): Promise<void> => {
  try {
    // Update the donation status
    const donationRef = ref(realtimeDb, `${DONATIONS_PATH}/${donationId}`);
    await update(donationRef, { status });

    // Update the stats when a donation's status changes
    const statsRef = ref(realtimeDb, DONATION_STATS_PATH);
    const statsSnapshot = await get(statsRef);

    if (statsSnapshot.exists()) {
      const currentStats = statsSnapshot.val();

      // Only update pending donations count
      if (status === 'completed') {
        // Decrement pending count
        await update(statsRef, {
          pendingDonations: Math.max(0, currentStats.pendingDonations - 1)
        });
      } else if (status === 'pending') {
        // Increment pending count
        await update(statsRef, {
          pendingDonations: currentStats.pendingDonations + 1
        });
      }
    }
  } catch (error) {
    console.error('Error updating donation status in Realtime Database:', error);
    throw error;
  }
};

/**
 * Subscribe to donation stats updates in real-time
 */
export const subscribeToDonationStats = (
  callback: (stats: {
    totalCollected: number;
    targetAmount: number;
    donationCount: number;
    pendingDonations: number;
  }) => void
) => {
  const statsRef = ref(realtimeDb, DONATION_STATS_PATH);

  // Initialize stats if they don't exist
  get(statsRef).then((snapshot) => {
    if (!snapshot.exists()) {
      set(statsRef, {
        totalCollected: 0,
        targetAmount: 100000,
        donationCount: 0,
        pendingDonations: 0
      });
    }
  });

  // Set up the listener
  const unsubscribe = onValue(statsRef, (snapshot) => {
    if (snapshot.exists()) {
      const stats = snapshot.val();
      console.log('REALTIME UPDATE: Donation stats changed:', stats);
      console.log('New total collected:', stats.totalCollected);
      callback(stats);
    } else {
      console.log('REALTIME UPDATE: No stats found, using defaults');
      const defaultStats = {
        totalCollected: 0,
        targetAmount: 100000,
        donationCount: 0,
        pendingDonations: 0
      };
      callback(defaultStats);
    }
  }, (error) => {
    console.error('Error subscribing to donation stats:', error);
  });

  // Return unsubscribe function to clean up the listener when no longer needed
  return unsubscribe;
};

/**
 * Subscribe to Recent Offers in real-time
 */
export const subscribeToRecentDonations = (
  callback: (donations: Donation[]) => void,
  limit: number = 10
) => {
  const donationsRef = ref(realtimeDb, DONATIONS_PATH);

  // Set up the listener
  const unsubscribe = onValue(donationsRef, (snapshot) => {
    if (snapshot.exists()) {
      const donations: Donation[] = [];

      // Convert the object to an array
      snapshot.forEach((childSnapshot) => {
        const donation = childSnapshot.val();
        donations.push({
          id: childSnapshot.key || '',
          ...donation
        });
      });

      // Sort by creation date (newest first) and limit the number of results
      const sortedDonations = donations
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, limit);

      callback(sortedDonations);
    } else {
      callback([]);
    }
  });

  // Return unsubscribe function to clean up the listener when no longer needed
  return unsubscribe;
};

/**
 * Clear all donations from Realtime Database
 */
export const clearAllRealtimeDonations = async (): Promise<void> => {
  try {
    // Remove all donations
    const donationsRef = ref(realtimeDb, DONATIONS_PATH);
    await remove(donationsRef);
    
    // Reset stats
    const statsRef = ref(realtimeDb, DONATION_STATS_PATH);
    await set(statsRef, {
      totalCollected: 0,
      targetAmount: 100000,
      donationCount: 0,
      pendingDonations: 0
    });
    
    console.log('All donations successfully deleted from Realtime Database');
  } catch (error) {
    console.error('Error clearing all donations from Realtime Database:', error);
    throw error;
  }
};

/**
 * Update the target amount in Realtime Database
 */
export const updateTargetAmount = async (newTargetAmount: number): Promise<void> => {
  try {
    // Update the target amount in stats
    const statsRef = ref(realtimeDb, DONATION_STATS_PATH);
    const statsSnapshot = await get(statsRef);
    
    if (statsSnapshot.exists()) {
      await update(statsRef, { targetAmount: newTargetAmount });
      console.log(`Target amount updated to ₹${newTargetAmount} in Realtime Database`);
    } else {
      // If stats don't exist yet, initialize them with the new target amount
      await set(statsRef, {
        totalCollected: 0,
        targetAmount: newTargetAmount,
        donationCount: 0,
        pendingDonations: 0
      });
      console.log(`Stats initialized with target amount ₹${newTargetAmount} in Realtime Database`);
    }
  } catch (error) {
    console.error('Error updating target amount in Realtime Database:', error);
    throw error;
  }
};
